import { AnimatedNumber } from '../AnimatedNumber';
import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { TrendGlyph } from './icons';
import { money } from './parts';
import type { ArtRenderer, CoinView } from './model';
import { CoinPreview } from './CoinPreview';
import { useCoinModal } from './CoinModal';
import { DEFAULT_RAIL_MOTION, RAIL_LIMIT, type RailMotion } from './railMotion';
import {
  DEFAULT_QUEUE_MOTION,
  motionEase,
  type QueueMotion,
} from './queueMotion';
import { useRailLayout } from './useRailLayout';
function RailArrival({
  ms,
  delay = 0,
  children,
}: {
  ms: number;
  delay?: number;
  children: ReactNode;
}) {
  const [entering, setEntering] = useState(true);
  const [enterDelay] = useState(delay);
  useEffect(() => {
    if (!entering) return;
    const timer = window.setTimeout(() => setEntering(false), enterDelay + ms);
    return () => window.clearTimeout(timer);
  }, [entering, enterDelay, ms]);
  return (
    <div
      className={'rail-arrival' + (entering ? ' is-entering' : '')}
      style={
        entering && enterDelay
          ? { animationDelay: enterDelay + 'ms' }
          : undefined
      }
    >
      {children}
    </div>
  );
}
type TokenRailProps = {
  side: 'left' | 'right';
  label: string;
  heading?: string;
  coins: CoinView[];
  now: number;
  art: ArtRenderer;
  motion?: RailMotion;
  queueMotion?: QueueMotion;
  action: { label: string; glyph: ReactNode; onClick: () => void };
};

export function TokenRail({
  side,
  label,
  heading,
  coins,
  now,
  art,
  action,
  motion = DEFAULT_RAIL_MOTION,
  queueMotion = DEFAULT_QUEUE_MOTION,
}: TokenRailProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const details = useCoinModal();
  const prefix = useId();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
  }, []);
  const shown = coins.slice(0, RAIL_LIMIT);
  const ids = JSON.stringify(shown.map((coin) => coin.id));
  const list = useRailLayout(ids, queueMotion);
  const candidate = details?.isOpen ? null : (hovered ?? focused);
  const open = shown.some((coin) => coin.id === candidate) ? candidate : null;
  const activeCoin = shown.find((coin) => coin.id === open);
  const lastOpen = useRef(open);
  useEffect(() => {
    lastOpen.current = open;
  }, [open]);
  useEffect(() => {
    const visible = new Set<string>(JSON.parse(ids));
    const keep = (id: string | null) => (id && visible.has(id) ? id : null);
    setHovered(keep);
    setFocused(keep);
  }, [ids]);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setHovered(null);
      setFocused(null);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [open]);
  return (
    <div
      className={
        'token-rail is-' +
        side +
        (heading ? ' has-heading' : '') +
        (open ? ' has-active' : '')
      }
      role="presentation"
      onMouseLeave={() => setHovered(null)}
      style={
        {
          // Reserve the largest supported hover layout before any token is focused.
          '--rail-height':
            64 *
              (Math.max(1, motion.hover.scale) +
                (RAIL_LIMIT - 1) * Math.max(1, motion.others.scale)) +
            RAIL_LIMIT * 12 +
            64 +
            'px',
          '--rail-ease': motionEase(0),
          '--queue-ease': motionEase(queueMotion.tapeBounce),
          '--enter-scale': String(queueMotion.enterScale),
          '--enter-blur': queueMotion.enterBlur + 'px',
          '--enter-ms': queueMotion.enterMs + 'ms',
          '--rail-preview-scale': String(motion.preview.scale),
          '--rail-preview-blur': motion.preview.blur + 'px',
          '--rail-preview-ms': motion.preview.ms + 'ms',
        } as CSSProperties
      }
    >
      {heading && <h2 className="rail-heading">{heading}</h2>}
      <ul ref={list} aria-label={label}>
        {shown.map((coin) => (
          <li
            key={coin.id}
            data-coin-id={coin.id}
            className={coin.id === open ? 'is-active' : ''}
            style={
              {
                '--chip-scale': open
                  ? coin.id === open
                    ? motion.hover.scale
                    : motion.others.scale
                  : 1,
                '--chip-blur':
                  (open
                    ? coin.id === open
                      ? motion.hover.blur
                      : motion.others.blur
                    : 0) + 'px',
                '--chip-ms':
                  (coin.id === (open ?? lastOpen.current)
                    ? motion.hover.ms
                    : motion.others.ms) + 'ms',
              } as CSSProperties
            }
          >
            <RailArrival
              ms={queueMotion.enterMs}
              delay={mounted.current ? queueMotion.tapeMs : 0}
            >
              <button
                type="button"
                className={'rail-chip' + (coin.id === open ? ' is-open' : '')}
                // A moving slot must not select itself under a stationary pointer.
                onMouseMove={() => setHovered(coin.id)}
                aria-haspopup="dialog"
                aria-label={
                  money(coin.marketCap) +
                  ' ' +
                  coin.name +
                  ' ($' +
                  coin.symbol +
                  ')' +
                  (coin.simulated ? ' · simulated' : '')
                }
                aria-describedby={
                  coin.id === open ? prefix + coin.id : undefined
                }
                onClick={(event) => {
                  setHovered(null);
                  setFocused(null);
                  details?.open(coin, event.currentTarget);
                }}
                onFocus={(event) => {
                  if (
                    !details?.isOpen &&
                    event.currentTarget.matches(':focus-visible')
                  )
                    setFocused(coin.id);
                }}
                onBlur={() => setFocused(null)}
              >
                {art(coin, 'rail-art')}
                <span className="rail-cap">
                  <AnimatedNumber value={money(coin.marketCap)} />
                </span>
                <span
                  className={
                    'rail-trend ' +
                    ((coin.changePercent ?? 0) >= 0 ? 'is-up' : 'is-down')
                  }
                >
                  <TrendGlyph />
                </span>
                {coin.simulated && (
                  <span
                    className="rail-game"
                    title="Game token"
                    aria-hidden="true"
                  >
                    <span className="game-badge-label">G</span>
                  </span>
                )}
                <span className="sr-only">
                  {coin.name} (${coin.symbol}){coin.simulated && ' · simulated'}
                </span>
              </button>
            </RailArrival>
          </li>
        ))}
      </ul>
      <RailArrival ms={queueMotion.enterMs}>
        <button
          type="button"
          className="rail-action"
          onClick={action.onClick}
          aria-label={action.label}
          title={action.label}
        >
          {action.glyph}
        </button>
      </RailArrival>
      {activeCoin && (
        <CoinPreview
          key={activeCoin.id}
          id={prefix + activeCoin.id}
          coin={activeCoin}
          now={now}
          side={side}
        />
      )}
    </div>
  );
}
